import { useEffect, useState, memo } from 'react'
import { ExternalLink, RefreshCw } from 'lucide-react'
import { apiFetch } from '../lib/api'
import { buttonClass } from './ui/Button';

const FINVIZ_URL = 'https://finviz.com/bubbles?idx=sp500'

interface Snapshot {
  capturedAt: string
}

function FinvizBubbleMap() {
  const [snapshot, setSnapshot] = useState<Snapshot | null>(null)
  const [blobUrl, setBlobUrl] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)

  const fetchSnapshot = async () => {
    setLoading(true)
    setError(false)

    // Revoke previous blob URL to free memory
    setBlobUrl(prev => { if (prev) URL.revokeObjectURL(prev); return null })

    try {
      const metaRes = await apiFetch('/api/finviz-snapshot')
      if (!metaRes.ok) throw new Error('No snapshot')
      const data: Snapshot = await metaRes.json()

      // Fetch image through authenticated endpoint, convert to blob URL
      const imgRes = await apiFetch(`/api/finviz-snapshot/image?t=${new Date(data.capturedAt).getTime()}`)
      if (!imgRes.ok) throw new Error('Image unavailable')
      const blob = await imgRes.blob()

      setSnapshot(data)
      setBlobUrl(URL.createObjectURL(blob))
    } catch {
      setError(true)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchSnapshot()
    return () => { setBlobUrl(prev => { if (prev) URL.revokeObjectURL(prev); return null }) }
  }, [])

  const capturedLabel = snapshot?.capturedAt
    ? new Date(snapshot.capturedAt).toLocaleString('th-TH', {
        day: '2-digit', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
      })
    : null

  if (loading) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    )
  }

  if (error || !blobUrl) {
    return (
      <div className="w-full h-full flex flex-col items-center justify-center gap-4 px-6 text-center">
        <p className="text-sm text-gray-500 dark:text-gray-400">
          ยังไม่มีภาพ — bot จะส่งภาพทุกเช้า หรือสั่ง /bubbles ใน Telegram
        </p>
        <a
          href={FINVIZ_URL}
          target="_blank"
          rel="noopener noreferrer"
          className={buttonClass('primary', 'md')}
        >
          <ExternalLink className="w-4 h-4" />
          เปิด Finviz โดยตรง
        </a>
      </div>
    )
  }

  return (
    <div className="relative w-full h-full">
      <img
        src={blobUrl}
        alt="Finviz S&P 500 Bubble Chart"
        className="w-full h-full object-contain rounded-xl"
      />
      {capturedLabel && (
        <div className="absolute bottom-2 right-2 flex items-center gap-2">
          <span className="text-[11px] text-gray-400 dark:text-gray-400 bg-white/80 dark:bg-gray-800/80 px-2 py-0.5 rounded-md backdrop-blur-sm">
            อัปเดต {capturedLabel}
          </span>
          <button
            onClick={fetchSnapshot}
            className="p-1.5 rounded-lg bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm text-gray-500 hover:text-blue-500 transition-colors"
            title="Refresh"
          >
            <RefreshCw className="w-3.5 h-3.5" />
          </button>
        </div>
      )}
    </div>
  )
}

export default memo(FinvizBubbleMap)
